import { useMemo, useState } from 'react'
import { NavBar } from 'components/NavBar/NavBar'
import { Flex, PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { Head1 } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { toast } from 'react-toastify'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { IFoodKitchen, IKitchenConfirmation } from 'types/foodLogistics/foodKitchen.interface'
import { IFoodException } from 'types/foodLogistics/foodException.interface'
import { IFoodMealRequest } from 'types/foodLogistics/foodMealRequest.interface'
import { IMealWindow } from 'types/foodLogistics/mealWindow.interface'
import useQueryPagination from 'hooks/useQueryPagination'
import { getFoodEligibleEmployees, getFoodKitchens, getFoodMealWindows } from 'features/food/foodSlice'
import { DashboardTab } from './tabs/DashboardTab'
import { ExceptionsTab } from './tabs/ExceptionsTab'
import { MealRequestsTab } from './tabs/MealRequestsTab'
import { ProfilesTab } from './tabs/ProfilesTab'
import { ConfigurationTab } from './tabs/ConfigurationTab'
import { HistoryTab } from './tabs/HistoryTab'

const FOOD_TABS = ['Dashboard', 'Exceptions', 'Meal Requests', 'Profiles', 'Configuration', 'History']

export const Food = () => {
  const { mealWindows, logisticsKitchens } = useSelector((store: RootState) => store.food)
  const [activeTab, setActiveTab] = useState('Dashboard')
  const [exceptions, setExceptions] = useState<IFoodException[]>([])
  const [requests, setRequests] = useState<IFoodMealRequest[]>([])
  const [confirmations] = useState<{ [key: string]: IKitchenConfirmation }>({})
  const [createMealRequestOpen, setCreateMealRequestOpen] = useState(false)
  const [configurationToolbar, setConfigurationToolbar] = useState<React.ReactNode>(null)

  const params = useMemo(() => ({ page: 1, limit: 200 }), [])

  useQueryPagination({
    fetchFunctions: [getFoodMealWindows, getFoodKitchens, getFoodEligibleEmployees],
    cleanUpFunctions: [],
    params,
  })

  const windows: IMealWindow[] = useMemo(
    () =>
      (mealWindows.data || []).map((w) => ({
        id: w.id,
        name: w.name,
        time: `${String(w.serviceStart || '').slice(0, 5)}–${String(w.serviceEnd || '').slice(0, 5)}`,
        cutoff: String(w.cutoffTime || '').slice(0, 5),
        planned: 0,
        ordered: 0,
        status: 'Planned',
        locked: false,
        menus: {},
      })),
    [mealWindows.data],
  )

  const kitchens: IFoodKitchen[] = useMemo(
    () =>
      (logisticsKitchens.data || []).map((k) => ({
        id: k.id,
        name: k.name,
        caps: (k.dietaryTagCapabilities || []).map((t) => t.name),
        packout: k.packoutSupport,
        softCap: k.softCapacity,
        standbyEnabled: k.standbyEnabled,
        standby: (k.dietaryTagCapabilities || []).map((t) => ({ tags: [t.name], qty: 0, min: 0 })),
        operator: k.operatorType === 2 ? 'Contractor' : 'Internal',
      })),
    [logisticsKitchens.data],
  )

  const kitchenOrdered = useMemo(() => {
    const map: { [windowId: number]: { [kitchenId: number]: number } } = {}
    windows.forEach((w) => {
      map[w.id] = {}
      kitchens.forEach((k) => {
        map[w.id][k.id] = 0
      })
    })
    return map
  }, [windows, kitchens])

  const resolveException = (exc: IFoodException, actionKey: string, comment: string) => {
    setExceptions((es) => es.map((e) => (e.id === exc.id ? { ...e, status: 'Resolved', res: actionKey } : e)))
    toast.success(`Exception #${exc.id} resolved via ${actionKey}${comment ? ' — ' + comment : ''}`)
  }

  const decideRequest = (id: string, approve: boolean) => {
    setRequests((rs) =>
      rs.map((r) =>
        r.id === id ? { ...r, status: approve ? 'Approved' : 'Rejected', routing: approve ? 'Standby' : '—' } : r,
      ),
    )
    toast.success(approve ? `${id} approved · routing: STANDBY` : `${id} rejected`)
  }

  const goCreateRequest = () => {
    setActiveTab('Meal Requests')
    setCreateMealRequestOpen(true)
  }

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    if (tab !== 'Configuration') setConfigurationToolbar(null)
  }

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <Flex between center mb='16'>
          <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>Food</Head1>
        </Flex>
        <Flex between alignEnd mb='16' width='100%' wrap gap='1rem'>
          <TabsContainer>
            {FOOD_TABS.map((tab) => (
              <Tabs key={tab} active={activeTab === tab} onClick={() => handleTabChange(tab)}>
                {tab}
              </Tabs>
            ))}
          </TabsContainer>
          {activeTab === 'Dashboard' && (
            <Button variant='primary' style={{ width: '220px' }} onClick={goCreateRequest}>
              Create Meal Request
            </Button>
          )}
          {activeTab === 'Meal Requests' && (
            <Button variant='primary' style={{ width: '220px' }} onClick={() => setCreateMealRequestOpen(true)}>
              Create Meal Request
            </Button>
          )}
          {activeTab === 'Configuration' && configurationToolbar}
          {activeTab === 'History' && <Button variant='primary'>Export</Button>}
        </Flex>
        {activeTab === 'Dashboard' && (
          <DashboardTab
            windows={windows}
            kitchens={kitchens}
            kitchenOrdered={kitchenOrdered}
            confirmations={confirmations}
            exceptions={exceptions}
            openExceptions={() => setActiveTab('Exceptions')}
          />
        )}
        {activeTab === 'Exceptions' && (
          <ExceptionsTab exceptions={exceptions} resolveException={resolveException} />
        )}
        {activeTab === 'Meal Requests' && (
          <MealRequestsTab
            requests={requests}
            setRequests={setRequests}
            decideRequest={decideRequest}
            kitchens={kitchens}
            windows={windows}
            createOpen={createMealRequestOpen}
            setCreateOpen={setCreateMealRequestOpen}
          />
        )}
        {activeTab === 'Profiles' && <ProfilesTab />}
        {activeTab === 'Configuration' && (
          <ConfigurationTab
            onToolbarChange={setConfigurationToolbar}
          />
        )}
        {activeTab === 'History' && <HistoryTab />}
      </PageWrapper>
    </>
  )
}
